import { SquarePenIcon, XIcon } from "lucide-react";
import { type MouseEvent, type ReactNode, useEffect, useState } from "react";

import { Tooltip, TooltipPopup, TooltipTrigger } from "../ui/tooltip";

import { formatWorkingDurationLabel } from "../Sidebar.logic";

// Floats at the row's right edge, vertically centered, while the jump modifier is
// held. It overlays the row so it neither displaces the status/time label nor shifts
// any layout when it appears, and takes no pointer events so the settle/un-settle
// buttons it can overlap still get their clicks.
export function JumpHintBadge(props: { label: string }) {
  return (
    <span
      aria-hidden
      className="pointer-events-none absolute right-1.5 top-1/2 z-10 inline-flex h-5 -translate-y-1/2 items-center rounded-full border border-border/80 bg-background/95 px-1.5 font-mono text-3xs font-medium tracking-tight text-foreground shadow-sm"
    >
      {props.label}
    </span>
  );
}

// Self-ticking so only this span re-renders each second, not the whole row.
export function WorkingDuration(props: { startedAt: string | null }) {
  const startedMs = props.startedAt !== null ? Date.parse(props.startedAt) : Number.NaN;
  const [, setTick] = useState(0);
  useEffect(() => {
    if (Number.isNaN(startedMs)) return;
    const id = window.setInterval(() => setTick((tick) => tick + 1), 1_000);
    return () => window.clearInterval(id);
  }, [startedMs]);
  if (Number.isNaN(startedMs)) return null;
  return <span className="tabular-nums">{formatWorkingDurationLabel(Date.now() - startedMs)}</span>;
}

/** Discards an unsent draft; it shows while the row is hovered or focused. */
export function DiscardDraftButton(props: { onDiscard: (event: MouseEvent) => void }) {
  return (
    <span className="ml-auto flex h-5 min-w-5 shrink-0 items-center justify-end">
      <Tooltip>
        <TooltipTrigger
          render={
            <button
              type="button"
              aria-label="Discard draft"
              onClick={props.onDiscard}
              className="pointer-events-none inline-flex cursor-pointer items-center rounded-md bg-transparent px-1 text-muted-foreground opacity-0 transition-opacity hover:text-foreground focus-visible:pointer-events-auto focus-visible:opacity-100 group-hover/sidebar-row:pointer-events-auto group-hover/sidebar-row:opacity-100"
            >
              <XIcon className="size-3" />
            </button>
          }
        />
        <TooltipPopup side="top">Discard draft</TooltipPopup>
      </Tooltip>
    </span>
  );
}

/** An unsent draft as one quiet line, like the threads beside it: the pen, then the prompt. */
export function OneLineDraftBody(props: { preview: ReactNode; discard: ReactNode }) {
  return (
    <div className="relative z-10 flex h-9 min-w-0 items-center gap-2 px-(--sidebar-row-content-inset)">
      <SquarePenIcon aria-hidden className="size-3.5 shrink-0 text-sidebar-muted-foreground" />
      <span aria-hidden className="min-w-0 flex-1 truncate text-sm font-medium text-foreground/90">
        {props.preview}
      </span>
      {props.discard}
    </div>
  );
}
